import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core"
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable"
import { Form, Input, Select, Space } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { handleDragEnd, handleDragOver } from "../../helpers/dragHelpers"
import { useLesson } from "../../hooks/lesson-context"
import { OrderCorrectlyDroppable } from "./OrderCorrectlyDroppable"

export function OrderCorrectly({
  question,
  questionIndex,
  form,
  dispatch,
  isFacilitate = false,
  disabled = false,
  required = false,
  inReview = false,
  trackQuestionSubmission,
}) {
  const { state: ctxState, dispatch: ctxDispatch } = useLesson()
  const { t } = useTranslation()
  const [hasTouched, setHasTouched] = useState(false)
  const [activeId, setActiveId] = useState(null)
  const [isDesktop, setIsDesktop] = useState(true)

  useEffect(() => {
    const mql = window.matchMedia("(min-width: 992px)")
    setIsDesktop(mql.matches)
    const handler = (e) => setIsDesktop(e.matches)
    mql.addEventListener("change", handler)
    return () => mql.removeEventListener("change", handler)
  }, [])

  const [itemGroups, setItemGroups] = useState(() => {
    const answers = form.getFieldValue(question.uid) || []
    const ansUids = answers.map((x) => x?.uid).filter(Boolean) || []

    const filteredQuestions = question.answers.filter((option) => !ansUids.includes(option?.uid))

    return {
      answers,
      options: filteredQuestions,
    }
  })

  useEffect(() => {
    const validAnswers = itemGroups?.answers?.filter(Boolean) || []
    if (validAnswers.length < question.answers?.length) {
    } else {
      if (hasTouched) {
        if (!ctxState?.actionRunning) {
          trackQuestionSubmission({
            data: validAnswers,
            typeUid: question.uid,
            type: question.type,
          })
        }
      }
    }

    form.setFieldsValue({
      [question.uid]: validAnswers,
    })
  }, [itemGroups?.answers, hasTouched])

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragStart = ({ active }) => {
    if (!hasTouched) {
      setHasTouched(true)
    }
    return setActiveId(active.id)
  }

  const handleDragCancel = () => setActiveId(null)

  const vertical = question?.answers?.some((x) => x.content?.title?.length > 65)

  const handleSelectChange = (val, i) => {
    setHasTouched(true)
    const newAnswers = [...(itemGroups.answers || [])]
    while (newAnswers.length <= i) {
      newAnswers.push(null)
    }

    if (!val) {
      newAnswers[i] = null
    } else {
      const selectedOption = question.answers.find((a) => a.uid === val)
      const existingIndex = newAnswers.findIndex((a) => a?.uid === val)
      if (existingIndex !== -1) {
        newAnswers[existingIndex] = newAnswers[i]
      }
      newAnswers[i] = selectedOption
    }

    const ansUids = newAnswers.map((x) => x?.uid).filter(Boolean)
    const filteredOptions = question.answers.filter((option) => !ansUids.includes(option?.uid))

    setItemGroups({ answers: newAnswers, options: filteredOptions })
  }

  return (
    <>
      <p className="instruction">{isDesktop ? t("dragTo") : t("selectToOrder", "Select to order correctly")}</p>
      <Form.List name={question.uid} className="sr-only">
        {(fields) => {
          return (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Space className="sr-only" key={key} style={{ display: "flex", marginBottom: 8 }} align="baseline">
                  <Form.Item hidden {...restField} name={[name, "uid"]}>
                    <Input placeholder="uid" />
                  </Form.Item>
                  <Form.Item hidden {...restField} name={[name, "correct"]} className="">
                    <Input placeholder="correct" />
                  </Form.Item>
                  <Form.Item hidden {...restField} name={[name, "title"]} className="">
                    <Input placeholder="title" />
                  </Form.Item>
                </Space>
              ))}
            </>
          )
        }}
      </Form.List>
      {inReview ? (
        <div className="dropzone empty">
          {form.getFieldValue(question.uid) &&
            form.getFieldValue(question.uid).map((ans, i) => (
              <div
                className={`missing__opt ${
                  isFacilitate !== "true" && i === parseInt(ans.correct)
                    ? "missing__opt--correct"
                    : "missing__opt--wrong"
                }`}
                key={ans.uid}
              >
                {ans?.translations?.["1"]?.title || ans.content?.title || "No title found"}
              </div>
            ))}
        </div>
      ) : (
        <>
          {itemGroups ? (
            isDesktop ? (
              <DndContext
                sensors={sensors}
                onDragStart={handleDragStart}
                onDragCancel={handleDragCancel}
                onDragOver={({ active, over }) => handleDragOver({ active, over, setItemGroups })}
                onDragEnd={(props) =>
                  handleDragEnd({
                    ...props,
                    setActiveId,
                    setItemGroups,
                    activeId,
                    itemGroups,
                  })
                }
              >
                {Object.keys(itemGroups).map((group) => (
                  <OrderCorrectlyDroppable
                    vertical={vertical}
                    id={group}
                    items={itemGroups[group].filter(Boolean)}
                    activeid={activeId}
                    key={group}
                    style={
                      vertical
                        ? {
                            flexDirection: "column",
                            alignItems: "start",
                            gap: "1rem",
                          }
                        : {}
                    }
                    className={group === "answers" ? "dropzone--smallMTop empty" : ""}
                  />
                ))}
              </DndContext>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  marginTop: "1rem",
                }}
              >
                {Array.from({ length: question.answers?.length || 0 }).map((_, i) => {
                  const selectedItem = itemGroups.answers[i]
                  return (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                      }}
                    >
                      <div style={{ fontWeight: "bold", width: "24px" }}>{i + 1}.</div>
                      <Select
                        value={selectedItem?.uid || null}
                        style={{ flex: 1 }}
                        placeholder={t("selectAnOption", "Select an option")}
                        allowClear
                        onChange={(val) => handleSelectChange(val, i)}
                        disabled={disabled}
                      >
                        {question.answers.map((opt) => (
                          <Select.Option key={opt.uid} value={opt.uid}>
                            {opt.translations?.["1"]?.title || opt.content?.title || "No title found"}
                          </Select.Option>
                        ))}
                      </Select>
                    </div>
                  )
                })}
              </div>
            )
          ) : null}
        </>
      )}
    </>
  )
}
